import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import {
  Button,
  ButtonType,
  MenuItem,
  TickFilled,
  ViewFilled,
} from '@access-erp/ui-components';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';

import useAddPurchaseOrder from './useAddPurchaseOrder';
import { validationSchema, Fields, CustomerOption, StatusOption, CurrencyOption } from './validationSchema';
import useSearchCustomers from '@/pages/AddContract/hooks/useSearchCustomers';

import styles from './add-purchase-order.module.scss';
import { filterPurchaseOrdersStatuses } from '@/config/statuses.config';
import { CurrencyType } from '@/graphql/contracts/_generated_/contracts';
import { browserHistory } from '@/routing/browserHistory';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { NumberUtils } from '@/utils/numbers.utils';

interface AddPurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (createdPurchaseOrder: any) => void;
  initialPoNumber?: string;
  customerCode?: string;
  customerId?: string;
  customerName?: string;
  currency?: string;
  customers?: CustomerOption[];
  statuses?: StatusOption[];
  enableCustomerSelection?: boolean;
  currencies?: CurrencyType[];
  refreshGrid?: () => void;
}

export default function AddPurchaseOrderModal({
  isOpen,
  onClose,
  onSuccess,
  initialPoNumber = '',
  customerCode,
  customerId = '',
  customerName,
  currency = '',
  statuses = filterPurchaseOrdersStatuses,
  enableCustomerSelection = false,
  currencies = [],
  refreshGrid
}: AddPurchaseOrderModalProps) {
  const { addNewPurchaseOrder } = useAddPurchaseOrder();
  const { searchCustomers, searchCustomersData } = useSearchCustomers();

  const currenciesOptions = useMemo(() => {
    return currencies.map((currencyItem) => ({
      label: currencyItem.formattedCurrency,
      value: currencyItem.currency,
    })) as CurrencyOption[];
  }, [currencies]);

  const customersOptions = useMemo(() => {
    return searchCustomersData.map((option) => ({
      label: `${option?.customerName} (${option.customerAccountNumber})`,
      value: option.customerID,
      accountNumber: option.customerAccountNumber,
    }));
  }, [searchCustomersData]);

  const initialValues: Fields = {
    customerId: customerId,
    poNumber: initialPoNumber,
    poDate: null,
    poStartDate: null,
    poEndDate: null,
    poTotalValue: null,
    poStatus: statuses[0]?.value,
    poNote: '',
    poCurrency: currency,
    customerAccountNumber: '',
  };

  const handleSubmit = (values: Fields) => {
    const successCallback = enableCustomerSelection && refreshGrid
      ? () => {
        refreshGrid();
        onClose();
      }
      : (createdPurchaseOrder: any) => {
        if (onSuccess) {
          onSuccess(createdPurchaseOrder);
        }
        onClose();
      };

    addNewPurchaseOrder(
      {
        input: {
          customerId: values.customerId,
          poNumber: values.poNumber,
          poDate: values.poDate,
          poStartDate: values.poStartDate,
          poEndDate: values.poEndDate,
          poTotalValue: NumberUtils.parseNumericInput(
            values.poTotalValue !== null ? values.poTotalValue.toString() : ''
          ),
          poStatus: values.poStatus as any,
          poNote: values.poNote,
          poCurrency: values.poCurrency,
        },
      },
      successCallback
    );
  };

  const redirectToContractLines = (values: Fields) => {
    addNewPurchaseOrder(
      {
        input: {
          customerId: values.customerId,
          poNumber: values.poNumber,
          poDate: values.poDate,
          poStartDate: values.poStartDate,
          poEndDate: values.poEndDate,
          poTotalValue: NumberUtils.parseNumericInput(
            values.poTotalValue !== null ? values.poTotalValue.toString() : ''
          ),
          poStatus: values.poStatus as any,
          poNote: values.poNote,
          poCurrency: values.poCurrency,
        },
      },
      () => {
        const link =
          '/purchase-orders/contract-lines?' +
          'invoiceToAccountNumber=' + values.customerAccountNumber +
          '&invoiceToAccountId=' + values.customerId +
          '&poNumber=' + values.poNumber +
          '&poCurrency=' + values.poCurrency;
        onClose();
        browserHistory.push(getParameterizedLink(link));
      }
    );
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <AgModalWindow
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Purchase Order"
      contentStyle={{ minWidth: '500px', maxWidth: '600px', width: '560px' }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validationContext={{ enableCustomerSelection }}
        onSubmit={() => { }}
        validateOnMount={true}
        validateOnChange={true}
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched, isValid }) => (
          <Form>
            <div className={styles['dropdown-margin']}>
              {enableCustomerSelection ? (
                <ModalFormDropdown<CustomerOption>
                  id="customerId"
                  labelTitle="Select a Customer"
                  requiredIcon={true}
                  optionsData={customersOptions}
                  getOptionLabel={(option) => option?.label || ''}
                  getOptionValue={(option) => option?.value || ''}
                  selectedOption={customersOptions.find(c => c.value === values.customerId) || null}
                  isSearchable={true}
                  handleChange={(selectedOption: CustomerOption | null) => {
                    const selectedCustomer = customersOptions.find(
                      (customer) => customer.value === selectedOption?.value
                    );
                    const selectedAccountNumber = selectedCustomer?.accountNumber;

                    setFieldValue('customerAccountNumber', selectedAccountNumber || '');
                    setFieldValue('customerId', selectedOption?.value || '');
                    setFieldTouched('customerId', true, false);
                  }}
                  handleInputChange={(newValue: string) => {
                    if (newValue.length > 0) {
                      searchCustomers(newValue);
                    }
                  }}
                  validationMessage={touched.customerId && errors.customerId ? errors.customerId : undefined}
                />
              ) : (
                <ModalFormTextInput
                  id="customerCode"
                  labelTitle="Selected Customer"
                  requiredIcon={true}
                  value={`${customerName} (${customerCode})`}
                  disabled={true}
                />
              )}

              <div className={styles['text-wrapper']}>
                <ModalFormTextInput
                  id="poNumber"
                  labelTitle="Number"
                  requiredIcon={true}
                  value={values.poNumber}
                  onChange={(e) => {
                    setFieldTouched('poNumber', true, false);
                    setFieldValue('poNumber', e.target.value);
                  }}
                  validationMessage={touched.poNumber && errors.poNumber ? errors.poNumber : undefined}
                />

                {enableCustomerSelection ? (
                  <ModalFormDropdown<CurrencyOption>
                    id="poCurrency"
                    labelTitle="Currency"
                    requiredIcon={true}
                    optionsData={currenciesOptions}
                    getOptionLabel={(option) => option?.label || ''}
                    getOptionValue={(option) => option?.value || ''}
                    selectedOption={currenciesOptions.find(c => c.value === values.poCurrency) || null}
                    handleChange={(selectedOption: CurrencyOption | null) => {
                      setFieldValue('poCurrency', selectedOption?.value || '');
                      setFieldTouched('poCurrency', true, false);
                    }}
                    isSearchable={true}
                    validationMessage={touched.poCurrency && errors.poCurrency ? errors.poCurrency : undefined}
                  />
                ) : (
                  <ModalFormTextInput
                    id="poCurrency"
                    labelTitle="Currency"
                    requiredIcon={true}
                    value={values.poCurrency}
                    disabled={true}
                    validationMessage={touched.poCurrency && errors.poCurrency ? errors.poCurrency : undefined}
                  />
                )}
              </div>

              <div className={styles['text-wrapper']}>
                <ModalFormTextInput
                  id="poTotalValue"
                  labelTitle="Total Value"
                  value={values.poTotalValue?.toString() || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFieldTouched('poTotalValue', true, false);
                    setFieldValue('poTotalValue', value);
                  }}
                  validationMessage={touched.poTotalValue && errors.poTotalValue ? errors.poTotalValue : undefined}
                  style={{ textAlign: 'right' }}
                />
                <div></div>
              </div>

              <div className={styles['text-wrapper']}>
                <ModalFormDatepicker
                  id="poDate"
                  labelTitle="Date"
                  value={values.poDate}
                  onChange={({ value }) => setFieldValue('poDate', value)}
                  validationMessage={touched.poDate && errors.poDate ? errors.poDate : undefined}
                />

                <ModalFormDatepicker
                  id="poStartDate"
                  labelTitle="Start Date"
                  value={values.poStartDate}
                  onChange={({ value }) => {
                    setFieldTouched('poStartDate', true, false);
                    setFieldValue('poStartDate', value);
                  }}
                  validationMessage={touched.poStartDate && errors.poStartDate ? errors.poStartDate : undefined}
                />

                <ModalFormDatepicker
                  id="poEndDate"
                  labelTitle="End Date"
                  value={values.poEndDate}
                  onChange={({ value }) => {
                    setFieldTouched('poEndDate', true, false);
                    setFieldValue('poEndDate', value);
                  }}
                  validationMessage={touched.poEndDate && errors.poEndDate ? errors.poEndDate : undefined}
                />
              </div>

              <div className={styles['text-wrapper']}>
                <ModalFormDropdown<StatusOption>
                  id="poStatus"
                  labelTitle="Status"
                  requiredIcon={true}
                  optionsData={statuses}
                  getOptionLabel={(option) => option?.label || ''}
                  getOptionValue={(option) => option?.value || ''}
                  selectedOption={statuses.find(s => s.value === values.poStatus) || null}
                  handleChange={(selectedOption: StatusOption | null) => {
                    setFieldValue('poStatus', selectedOption?.value || '');
                  }}
                  isSearchable={true}
                  validationMessage={touched.poStatus && errors.poStatus ? errors.poStatus : undefined}
                />
                <div></div>
              </div>

              <div>
                <ModalFormTextarea
                  id="poNote"
                  labelTitle="Note"
                  value={values.poNote}
                  onChange={(e) => setFieldValue('poNote', e.target.value)}
                  validationMessage={touched.poNote && errors.poNote ? errors.poNote : undefined}
                />
              </div>
            </div>
            {enableCustomerSelection ? (
              <div className={styles['modal-buttons']}>
                <Button
                  wrapperStyle={{ width: 'initial', flex: 'none' }}
                  buttonType={ButtonType.Secondary}
                  text="Cancel"
                  onClick={handleClose}
                />
                <Button
                  buttonType={ButtonType.Primary}
                  disabled={!isValid}
                  wrapperStyle={{ width: 'initial', flex: 'none' }}
                  text="Save"
                  onMenuItemClick={({ name }) => {
                    if (name === 'Save') {
                      handleSubmit(values);
                    }
                    if (name === 'Redirect') {
                      redirectToContractLines(values);
                    }
                  }}
                >
                  <MenuItem name="Save" icon={<TickFilled />} text="Save" />
                  <MenuItem name="Redirect" icon={<ViewFilled />} text="Save and View Contract Lines" />
                </Button>
              </div>
            ) : (
              <ActionButtonsWrapper
                leftButton={
                  <Button
                    buttonType={ButtonType.Secondary}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                }
                rightButton={
                  <Button
                    type="submit"
                    buttonType={ButtonType.Primary}
                    disabled={!isValid}
                    onClick={() => handleSubmit(values)}
                  >
                    Create and Assign
                  </Button>
                }
              />
            )}
          </Form>
        )}
      </Formik>
    </AgModalWindow >
  );
}
